import React from 'react';
import { Link } from 'react-router-dom';
import { luxuryDivisionImage, sportsEntImage, artOfRepImage, legacyHeroImage } from '../mock';

function Division({ id, image, eyebrow, title, description, cta, ctaHref, reverse, ctaTarget = 'contact' }) {
  return (
    <section id={id} className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#0b0b0b]">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="absolute inset-0 bg-black/45" />
      <div className={`relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 py-24 flex ${reverse ? 'justify-end' : 'justify-start'}`}>
        <div
          className="max-w-lg p-8 md:p-10"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0) 100%)',
          }}
        >
          {eyebrow && (
            <div className="inline-block mb-5 pb-1 border-b border-[#b39869]">
              <span className="tracking-[0.35em] text-[11px] text-[#b39869] uppercase">{eyebrow}</span>
            </div>
          )}
          <h2
            className="text-white text-3xl md:text-4xl mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
          >
            {title}
          </h2>
          {typeof description === 'string' ? (
            <p className="text-white/85 text-sm leading-relaxed mb-8">{description}</p>
          ) : (
            <div className="text-white/85 text-sm leading-relaxed mb-8 space-y-4">
              {description}
            </div>
          )}
          {cta && (
            ctaHref ? (
              <Link
                to={ctaHref}
                className="inline-block bg-[#b39869] hover:bg-[#9d8358] transition-colors duration-300 text-white uppercase tracking-[0.28em] text-xs px-8 py-4"
              >
                {cta}
              </Link>
            ) : (
              <a
                href={`#${ctaTarget}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(ctaTarget)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-block bg-[#b39869] hover:bg-[#9d8358] transition-colors duration-300 text-white uppercase tracking-[0.28em] text-xs px-8 py-4"
              >
                {cta}
              </a>
            )
          )}
        </div>
      </div>
    </section>
  );
}

export function ArtOfRepresentation() {
  return (
    <Division
      id="about"
      image={artOfRepImage}
      eyebrow="Explore"
      title="The Art of Representation"
      description={
        <>
          <p className="text-white font-medium">Luxury is not a price point &mdash; It is a standard.</p>
          <p>
            A discipline of precision, presence, and excellence. Hunt Global Collective unites discerning advisors
            who build with intention, identity, and mastery &mdash; elevating their craft and serving clientele with
            clarity, discretion, and quiet power.
          </p>
        </>
      }
      cta="Discover the Collective →"
      ctaTarget="leadership"
    />
  );
}

export function LuxuryDivision() {
  return (
    <Division
      id="luxury"
      image={luxuryDivisionImage}
      eyebrow="Division"
      title="Luxury Division"
      description="The Luxury Division is reserved for established advisors trusted with significant properties and discerning clientele. This designation provides access to global placement across premier media platforms — including Robb Report, Mansion Global, Barron’s, and The Wall Street Journal — paired with cinematic presentation, bespoke collateral, and a private advisory environment designed to elevate brand, exposure, and client experience."
      cta="Inquire Privately →"
      ctaHref="/contact"
    />
  );
}

export function LegacyDivision({ image, blurb }) {
  return (
    <Division
      id="legacy"
      image={image || legacyHeroImage}
      eyebrow="Legacy"
      title="Legacy is building beyond the transaction."
      description={
        blurb || (
          <>
            <p>
              Through leveraged income, equity and stock ownership, advisors have the opportunity
              to transform what they build today into long-term wealth—creating value that can
              continue to grow, extend beyond their own production, and become part of what they
              leave to their families for generations.
            </p>
            <p>
              Because true wealth is not simply what you earn. It is what you own, what continues
              to grow, and what you leave behind.
            </p>
          </>
        )
      }
      cta="Connect Privately →"
      ctaHref="/contact"
    />
  );
}

export function SportsEntertainmentDivision() {
  return (
    <Division
      id="private-clients"
      image={sportsEntImage}
      eyebrow="Division"
      title="Sports & Entertainment Division"
      description="The Sports & Entertainment Division is designed for advisors representing athletes, entertainers, founders, creators, and other public-facing clientele — where privacy, pace, and global mobility define representation. This division provides access to private-market opportunities, elite relocation partners, and UHNW advisory networks — delivered with discretion, rapid execution, and absolute confidentiality."
      cta="Request Private Access →"
      ctaHref="/contact"
      reverse
    />
  );
}
